import { useNavState } from '@/providers/NavStateProvider';
import { useEffect } from 'react';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { ScrollTop } from '@/components/element/ScrollTop';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useCurrentFixedAsset } from '@/providers/FixedAssetProvider';
import { Link, useNavigate } from 'react-router-dom';
import { SimpleTextFormat } from '@/components/element/SimpleTextFormat';
import { dateFormat } from '@/utils';
import { useDestroyFixedAsset } from '@/hooks/api/2030_destroy_fixed_asset';
import { useInvalidateFixedAssets } from '@/hooks/api/2000_fixed_assets';
import { toastApiError, toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';

export const FixedAsset = () => {
  const navigate = useNavigate();
  const { setTitle, setBreadcrumbs } = useNavState();
  const { _w_, me } = useCurrentWorkspace();
  const { setShowLoading } = useLoading();
  const { mutate, isSuccess, isPending, error } = useDestroyFixedAsset();
  const { invalidate } = useInvalidateFixedAssets(_w_!.identifier);

  useEffect(() => setTitle(`固定資産詳細`), []);
  useEffect(() => setBreadcrumbs([{ label: `固定資産管理`, path: `/w/${_w_!.identifier}/finance/fixed_assets` }]), []);

  useEffect(() => {
    if (isSuccess) {
      invalidate();
      toastSuccess('固定資産を削除しました');
      navigate(`/w/${_w_!.identifier}/finance/fixed_assets`);
    }
  }, [isSuccess]);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onDeleteClick = () => {
    setShowLoading(true);
    mutate({ identifier: _w_!.identifier, id: fixedAsset!.id });
  };

  const { fixedAsset, fixedAssetDetails } = useCurrentFixedAsset();

  return (
    <WorkspaceLayout>
      <ScrollTop />

      <div className="container-fluid py-4">
        <ModalConfirm
          modalId={`delConfirm`}
          okClicked={() => onDeleteClick()}
          message={'削除します。よろしいですか？'}
        />

        <div className="row">
          <div className="col-md-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-0">固定資産の概要</h5>
                </div>
                <div className="d-flex align-items-center">
                  {me!.admin && (
                    <>
                      <Link
                        to={`/w/${_w_!.identifier}/finance/fixed_assets/${fixedAsset!.id}/edit`}
                        className="btn btn-icon-only btn-outline-success me-1"
                      >
                        <i className="fas fa-edit"></i>
                      </Link>
                      <button
                        data-bs-toggle="modal"
                        data-bs-target={`#delConfirm`}
                        className="btn btn-icon-only btn-outline-danger"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="card-body pt-0 row">
                <div className="col-auto">
                  <div className="avatar avatar-xxl position-relative">
                    <img
                      src={fixedAsset!.image_file_url}
                      alt="profile_image"
                      className="w-100 border-radius-lg shadow-sm"
                    />
                  </div>
                </div>
                <div className="col-12 col-sm-8 mt-4 mt-sm-0">
                  <div className="form-group">
                    <label>固定資産名</label>
                    <p className="form-text text-muted text-sm ms-1">{fixedAsset!.name}</p>
                  </div>
                  <div className="form-group">
                    <label>メモ</label>
                    <p className="form-text text-muted text-sm ms-1">
                      <SimpleTextFormat content={fixedAsset!.description || ''} />
                    </p>
                  </div>
                  <div className="form-group">
                    <label>使用開始日</label>
                    <p className="form-text text-muted text-sm ms-1">{dateFormat({ date: fixedAsset!.start_date })}</p>
                  </div>
                  <div className="form-group">
                    <label>合計金額</label>
                    <p className="form-text text-muted text-sm ms-1">
                      ¥{fixedAsset!.payment_task_fixed_assets.reduce((sum, a) => sum + a.amount, 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="form-group">
                    <label>ステータス</label>
                    <p className="form-text text-muted text-sm ms-1">{fixedAsset!.fixed_asset_status.label}</p>
                  </div>
                </div>
              </div>

              <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                  戻る
                </Link>
              </div>
            </div>

            {fixedAssetDetails!.length > 0 && (
              <div className="card mb-4" id="basic-info">
                <div className="card-header d-flex align-items-center">
                  <div className="col-md-8">
                    <h5 className="mb-0">固定資産内訳</h5>
                  </div>
                </div>
                <div className="card-body pt-0">
                  <div className="table-responsive">
                    <table className="table align-items-center">
                      <thead>
                        <tr>
                          <th className="text-secondary text-xxs font-weight-bolder opacity-7 px-2">タスク名</th>
                          <th
                            className="text-secondary text-xxs font-weight-bolder opacity-7 px-2"
                            style={{ width: 200 }}
                          >
                            チーム
                          </th>
                          <th
                            className="text-secondary text-xxs font-weight-bolder opacity-7 px-2"
                            style={{ width: 60 }}
                          >
                            計上月
                          </th>
                          <th
                            className="text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end"
                            style={{ width: 100 }}
                          >
                            金額
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {fixedAssetDetails!.map((d, i) => (
                          <tr key={`fad-${i}`}>
                            <td className="text-xs text-dark font-weight-bold">{d.task_name}</td>
                            <td className="text-secondary text-xs">{d.team_name}</td>
                            <td className="text-secondary text-xs">
                              {d.year_month
                                .toString()
                                .match(/.{1,4}/g)!
                                .join('/')}
                            </td>
                            <td className="text-secondary text-xs text-end">¥{d.amount.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
